import React, { useState } from 'react';
import { X, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { itemsAPI } from '../../services/part2-api';
import { toast } from 'react-hot-toast';

const BulkTaxUpdateModal = ({ selectedItems, selectionMode, filters, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  
  const [formData, setFormData] = useState({
    tax_category: '',
    scope: 'sales',
  });

  const selectedCount = selectionMode === 'ids' ? selectedItems.length : 'filtered';

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.tax_category) {
      setError('Please select a tax category');
      return false;
    }
    return true;
  };

  const handleProceed = () => {
    if (!validateForm()) return;
    
    if ((selectionMode === 'ids' && selectedItems.length > 25) || selectionMode === 'filters') {
      setShowConfirm(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const payload = {
        mode: selectionMode,
        item_ids: selectionMode === 'ids' ? selectedItems : [],
        filters: selectionMode === 'filters' ? filters : {},
        operation: formData,
      };

      const response = await itemsAPI.bulkUpdateTax(payload);
      
      const { totalMatched, totalUpdated, failedIds } = response.data;
      
      if (failedIds.length > 0) {
        toast.error(`Updated ${totalUpdated} items, ${failedIds.length} failed`);
      } else {
        toast.success(`Successfully updated ${totalUpdated} item(s)`);
      }
      
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Bulk tax update error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to update tax categories';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getTaxLabel = (value) => {
    const labels = {
      'gst_0': 'GST 0%',
      'gst_5': 'GST 5%',
      'gst_12': 'GST 12%',
      'gst_18': 'GST 18%',
      'gst_28': 'GST 28%',
    };
    return labels[value] || value;
  };

  if (showConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Receipt className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-center mb-2">Confirm Bulk Update</h3>
            <p className="text-gray-600 text-center mb-4">
              You are about to update tax categories for <strong>{selectedCount}</strong> item(s).
              This action cannot be undone.
            </p>
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p className="text-sm"><strong>Tax Category:</strong> {getTaxLabel(formData.tax_category)}</p>
              <p className="text-sm"><strong>Applies to:</strong> {formData.scope === 'both' ? 'Sales & Purchase' : formData.scope === 'sales' ? 'Sales Tax' : 'Purchase Tax'}</p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowConfirm(false)}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Updating...' : 'Confirm Update'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Bulk Tax Update</h2>
              <p className="text-sm text-gray-600">
                {selectedCount} item(s) selected
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Tax Category */}
          <div>
            <Label>Tax Category</Label>
            <Select value={formData.tax_category} onValueChange={(value) => handleInputChange('tax_category', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select tax category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gst_0">GST 0%</SelectItem>
                <SelectItem value="gst_5">GST 5%</SelectItem>
                <SelectItem value="gst_12">GST 12%</SelectItem>
                <SelectItem value="gst_18">GST 18%</SelectItem>
                <SelectItem value="gst_28">GST 28%</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Scope */}
          <div>
            <Label>Apply to</Label>
            <Select value={formData.scope} onValueChange={(value) => handleInputChange('scope', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sales">Sales Tax Only</SelectItem>
                <SelectItem value="purchase">Purchase Tax Only</SelectItem>
                <SelectItem value="both">Both Sales & Purchase</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              This will update the tax category for {formData.scope === 'both' ? 'both sales and purchase taxes' : formData.scope === 'sales' ? 'sales tax' : 'purchase tax'}.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleProceed}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Tax'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkTaxUpdateModal;